import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from './AuthContext';
import {
  Company,
  getViewedCompany,
  storeViewedCompany,
  getResellerCompanyFilter,
  storeResellerCompanyFilter,
} from '../utils/resellerCompany';

export type { Company };

interface ResellerCompanyContextType {
  /** The company a reseller is currently viewing (null = viewing their own workspace). */
  viewedCompany: Company | null;
  setViewedCompany: (company: Company | null) => void;
  /** Within the merged "all my companies" view, narrow to one company (null = all). */
  companyFilter: number | null;
  setCompanyFilter: (tenantId: number | null) => void;
}

const ResellerCompanyContext = createContext<ResellerCompanyContextType | undefined>(undefined);

export const useResellerCompany = (): ResellerCompanyContextType => {
  const context = useContext(ResellerCompanyContext);
  if (context === undefined) {
    throw new Error('useResellerCompany must be used within a ResellerCompanyProvider');
  }
  return context;
};

interface ResellerCompanyProviderProps {
  children: ReactNode;
}

export const ResellerCompanyProvider: React.FC<ResellerCompanyProviderProps> = ({ children }) => {
  const { user, loading } = useAuth();
  const queryClient = useQueryClient();
  const [viewedCompany, setViewedCompanyState] = useState<Company | null>(() => getViewedCompany());
  const [companyFilter, setCompanyFilterState] = useState<number | null>(() => getResellerCompanyFilter());

  const setViewedCompany = useCallback((company: Company | null) => {
    storeViewedCompany(company);
    setViewedCompanyState(company);
    // A company filter only applies within the merged "all" view; reset it on any scope change.
    storeResellerCompanyFilter(null);
    setCompanyFilterState(null);
  }, []);

  const setCompanyFilter = useCallback((tenantId: number | null) => {
    storeResellerCompanyFilter(tenantId);
    setCompanyFilterState(tenantId);
    // Cached data was fetched through the previous filter — refetch through the new one.
    queryClient.invalidateQueries();
  }, [queryClient]);

  // Resellers: keep whatever they last picked (or none = own workspace); never
  // auto-select for them, and never clear their manual choice out from under them.
  // Team members (companyAccess): ensure a valid company is always selected, defaulting
  // to the first granted one, exactly like CompanyContext.tsx already does for
  // machineTenantIds viewers — self-heals if the stored pick is no longer in their grant
  // list (e.g. the reseller revoked it). Anyone else (including logged out): clear.
  // Skipped entirely while the initial auth check is in flight (user is briefly null
  // then) so a page reload doesn't wipe a valid selection before the profile resolves.
  useEffect(() => {
    if (loading) return;

    if (user?.isReseller) {
      // Resync in-memory state from storage (the source of truth) rather than trusting
      // whatever is already in React state: logout clears storage directly via
      // clearViewedCompany() without going through this context, and the auto-select
      // branch below can now write a team member's company into this same piece of
      // state — without this resync, that selection would otherwise survive in memory
      // and leak into a reseller who logs in next in the same tab.
      setViewedCompanyState(getViewedCompany());
      return;
    }

    const grants = Array.isArray(user?.companyAccess) ? user!.companyAccess : [];
    if (grants.length > 0) {
      const allowedIds = new Set(grants.map((g) => Number(g.tenantId)));
      const current = getViewedCompany();
      const currentValid = current !== null && allowedIds.has(current.tenantId);
      if (!currentValid) {
        const list = Array.isArray(user?.companyAccessCompanies) ? user!.companyAccessCompanies : [];
        const fallback = list[0] || { tenantId: Number(grants[0].tenantId), name: '' };
        setViewedCompany(fallback);
      }
    } else if (getViewedCompany() !== null) {
      setViewedCompany(null);
    }
  }, [user, loading, setViewedCompany]);

  return (
    <ResellerCompanyContext.Provider value={{ viewedCompany, setViewedCompany, companyFilter, setCompanyFilter }}>
      {children}
    </ResellerCompanyContext.Provider>
  );
};

export default ResellerCompanyContext;
